import { useRef, useState } from 'react';
import cn from 'classnames';
// Hooks
import { useMarkNotificationRead, useRemoveNotification } from 'hooks';
import { useOutsideClick } from 'hooks';
// Types
import { NotificationData } from 'helpers/notifications';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Text from 'ui3/Text/Text';
// Styles
import styles from './SingleNotificationActions.module.scss';

type SingleNotificationActionsProps = {
  notification: NotificationData;
  onUpdate: () => void;
};

const SingleNotificationActions = ({
  notification,
  onUpdate,
}: SingleNotificationActionsProps) => {
  const { markNotificationRead } = useMarkNotificationRead();
  const { removeNotification } = useRemoveNotification();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  const { id, isRead } = notification;

  const handleRemoveNotification = async () => {
    await removeNotification(id);
    onUpdate();
    setIsDropdownVisible(false);
  };

  const handleMarkNotificationRead = async () => {
    await markNotificationRead(id);
    onUpdate();
    setIsDropdownVisible(false);
  };

  useOutsideClick(dropdownRef, () => {
    setIsDropdownVisible(false);
  });

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  return (
    <div className={styles.root} ref={dropdownRef}>
      <button
        type="button"
        className={styles.dropdownButton}
        onClick={toggleDropdown}
      >
        <MillionsIcon name="moreAlternate" className={styles.dotsIcon} />
      </button>

      <ul
        aria-hidden={!isDropdownVisible}
        className={cn(styles.dropdown, {
          [styles.visible]: isDropdownVisible,
        })}
      >
        {!isRead && (
          <li>
            <button
              data-testid="remove-button"
              type="button"
              className={styles.option}
              onClick={handleMarkNotificationRead}
            >
              <MillionsIcon
                name="doubleCheck"
                className={cn(styles.optionIcon, styles.fillIcon)}
              />
              <Text variant="subtitle2" color="lights-medium">
                Mark as read
              </Text>
            </button>
          </li>
        )}
        <li>
          <button
            data-testid="remove-button"
            type="button"
            className={styles.option}
            onClick={handleRemoveNotification}
          >
            <MillionsIcon name="trash" className={styles.optionIcon} />
            <Text variant="subtitle2" color="lights-medium">
              Clear
            </Text>
          </button>
        </li>
      </ul>
    </div>
  );
};

export default SingleNotificationActions;
